import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search,
  Plus,
  Clock9,
  MapPin,
  User,
  MoreVertical,
  Calendar,
  SlidersHorizontal as Filters,
  X,
  Users,
  CheckCircle2,
  AlertCircle,
  Clock,
  ArrowRightLeft,
  Pencil,
  Trash2,
  Eye,
  Calculator,
  Loader2,
  XCircle,
  ChevronDown,
  ChevronUp,
  LayoutDashboard
} from 'lucide-react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import Pagination from '../../../components/Common/Pagination/Pagination';
import styles from './Overtime.module.css';
import OTAssignmentDrawer from './OTAssignmentDrawer';
import OTSalarySummaryModal from './OTSalarySummaryModal';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import api from '../../../api/axios';
import toast from 'react-hot-toast';

const MySwal = withReactContent(Swal);


const OTAssignments = ({ defaultTab }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialTab = defaultTab || (queryParams.get('tab') === 'requests' ? 'requests' : 'assignments');
  const initialSearch = queryParams.get('search') || '';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // Sync tab active state with location changes
  useEffect(() => {
    const tab = defaultTab || (queryParams.get('tab') === 'requests' ? 'requests' : 'assignments');
    setActiveTab(tab);
    const search = queryParams.get('search') || '';
    setSearchTerm(search);
  }, [location.search, defaultTab]);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role?.toLowerCase() === 'admin';

  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    site: null,
    date: '',
    status: null
  });

  const [siteOptions, setSiteOptions] = useState([]);

  // Fetch dropdown list options for sites dynamically
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await api.get('/projects-dropdown');
        if (response.data && response.data.data) {
          setSiteOptions(response.data.data.map(proj => ({
            value: proj.id,
            label: proj.project_name
          })));
        } else if (response.data) {
          setSiteOptions(response.data.map(proj => ({
            value: proj.id,
            label: proj.project_name
          })));
        }
      } catch (error) {
        console.error('Failed to load project site options for filter:', error);
      }
    };
    fetchSites();
  }, []);

  const [assignments, setAssignments] = useState([]);
  const [requests, setRequests] = useState([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Reset page to 1 when filters, search term, or active tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, activeTab]);

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/overtime/requests', {
        params: {
          per_page: 1000,
          site: filters.site,
          date: filters.date
        }
      });
      if (response.data && response.data.success) {
        const paginatedData = response.data.data;
        const rawList = paginatedData.data || paginatedData || [];
        const mapped = rawList.map(item => ({
          id: item.id,
          employee: item.employee ? item.employee.employee_name : 'Unknown',
          employee_id: item.employee ? item.employee.employee_id : item.employee_id,
          project_site: item.project ? item.project.project_name : 'N/A',
          project_site_id: item.project_id,
          date: item.ot_date ? new Date(item.ot_date).toISOString().split('T')[0] :
            (item.request_date ? new Date(item.request_date).toISOString().split('T')[0] : ''),
          assigned_by: item.assigned_by ? (item.assigned_by.name || 'Admin') : 'Admin',
          expected_start_time: item.expected_start_time ? item.expected_start_time.substring(0, 5) : '18:00',
          expected_end_time: item.expected_end_time ? item.expected_end_time.substring(0, 5) : '22:00',
          instructions: item.assignment_reason || 'N/A',
          assignment_time: item.created_at ? new Date(item.created_at).toISOString().replace('T', ' ').substring(0, 19) : '',
          status: item.status || 'assigned'
        }));
        setAssignments(mapped);
      }
    } catch (error) {
      console.error('Failed to load OT assignments:', error);
      toast.error('Failed to load overtime assignments');
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await api.get('/overtime/requests', {
        params: {
          per_page: 1000
        }
      });
      if (response.data && response.data.success) {
        const rawList = response.data.data?.data || response.data.data || [];
        const mapped = rawList.map(req => ({
          id: req.id,
          employee: req.employee ? req.employee.employee_name : `Employee ID: ${req.employee_id}`,
          employee_id: req.employee_id,
          project_site: req.project ? req.project.project_name : 'N/A',
          project_site_id: req.project_id,
          date: req.request_date ? new Date(req.request_date).toISOString().split('T')[0] : '-',
          reason: req.reason || req.remarks || req.assignment_reason || '-',
          status: req.status || 'pending'
        }));
        setRequests(mapped);
      }
    } catch (error) {
      console.error('Failed to fetch OT requests:', error);
      toast.error('Failed to load overtime requests');
    } finally {
      setLoading(false);
    }
  };

  // Initialize and load data based on active tab and filters
  useEffect(() => {
    if (activeTab === 'assignments') {
      fetchAssignments();
    } else {
      fetchRequests();
    }
  }, [filters, activeTab]);

  // Filter assignments based on search term
  const filteredAssignments = assignments.filter(record => {
    const matchesSearch =
      (record.employee && record.employee.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (record.project_site && record.project_site.toLowerCase().includes(searchTerm.toLowerCase())) ||
      String(record.id) === searchTerm;

    return matchesSearch;
  });

  const filteredRequests = requests.filter(record => {
    const matchesSearch =
      (record.employee && record.employee.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (record.project_site && record.project_site.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (record.reason && record.reason.toLowerCase().includes(searchTerm.toLowerCase())) ||
      String(record.id) === searchTerm;

    const matchesSite = !filters.site || Number(record.project_site_id) === Number(filters.site);
    const matchesDate = !filters.date || record.date === filters.date;
    const matchesStatus = !filters.status || record.status === filters.status;

    return matchesSearch && matchesSite && matchesDate && matchesStatus;
  });

  const paginatedAssignments = filteredAssignments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const paginatedRequests = filteredRequests.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const pendingCount = requests.filter(r => r.status === 'pending' || r.status === 'assigned' || r.status === 'in_progress').length;
  const approvedCount = requests.filter(r => r.status === 'approved' || r.status === 'completed').length;
  const uniqueRequestors = new Set(requests.map(r => r.employee_id)).size;

  const summaryData = activeTab === 'assignments'
    ? [
      {
        icon: <Clock9 size={20} />,
        label: 'Scheduled OT',
        value: String(filteredAssignments.length).padStart(2, '0'),
        theme: 'blue'
      },
      {
        icon: <Users size={20} />,
        label: 'Staff Assigned',
        value: String(new Set(filteredAssignments.map(a => a.employee_id)).size).padStart(2, '0'),
        theme: 'green'
      },
      {
        icon: <MapPin size={20} />,
        label: 'Active Sites',
        value: String(new Set(filteredAssignments.map(a => a.project_site_id)).size).padStart(2, '0'),
        theme: 'orange'
      }
    ]
    : [
      {
        icon: <AlertCircle size={20} />,
        label: 'Pending Requests',
        value: String(pendingCount).padStart(2, '0'),
        theme: 'orange'
      },
      {
        icon: <CheckCircle2 size={20} />,
        label: 'Approved Requests',
        value: String(approvedCount).padStart(2, '0'),
        theme: 'green'
      },
      {
        icon: <Users size={20} />,
        label: 'Requesting Staff',
        value: String(uniqueRequestors).padStart(2, '0'),
        theme: 'blue'
      }
    ];

  const getStatusClass = (status) => {
    const s = status?.toLowerCase();
    if (s === 'approved' || s === 'completed') return styles['status-approved'];
    if (s === 'rejected' || s === 'cancelled') return styles['status-rejected'];
    return styles['status-pending'];
  };

  const getStatusLabel = (status) => {
    const s = status?.toLowerCase();
    if (s === 'assigned') return 'Assigned';
    if (s === 'in_progress') return 'In Progress';
    if (s === 'completed') return 'Completed';
    if (s === 'cancelled') return 'Cancelled';
    if (s === 'approved') return 'Approved';
    if (s === 'rejected') return 'Rejected';
    return 'Pending';
  };

  const getStatusIcon = (status) => {
    const s = status?.toLowerCase();
    if (s === 'approved' || s === 'completed') return <CheckCircle2 size={12} />;
    if (s === 'rejected' || s === 'cancelled') return <XCircle size={12} />;
    return <AlertCircle size={12} />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  const handleRequestAction = (record, action) => {
    const isApprove = action === 'approve';

    MySwal.fire({
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: isApprove
        ? '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg> Yes, Approve</div>'
        : '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg> Yes, Reject</div>',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      input: !isApprove ? 'text' : undefined,
      inputPlaceholder: !isApprove ? 'Reason for rejection...' : undefined,
      inputValidator: !isApprove ? (value) => {
        if (!value) {
          return 'You must provide a reason for rejection!';
        }
      } : undefined,
      customClass: {
        popup: 'custom-swal-popup',
        actions: 'custom-swal-actions',
        confirmButton: isApprove ? 'custom-swal-confirm-approve' : 'custom-swal-confirm-reject',
        cancelButton: 'custom-swal-cancel',
        input: 'custom-swal-input',
      },
      html: `
        <style>
          .custom-swal-popup {
            border-radius: 16px !important;
            padding: 32px 24px 24px 24px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 440px !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          }
          @media (max-width: 480px) {
            .custom-swal-popup {
              width: calc(100vw - 32px) !important;
              padding: 24px 16px 20px 16px !important;
            }
            .custom-swal-actions {
              flex-direction: column !important;
              gap: 8px !important;
            }
            .custom-swal-confirm-approve,
            .custom-swal-confirm-reject,
            .custom-swal-cancel {
              width: 100% !important;
              justify-content: center !important;
            }
          }
          .custom-swal-actions {
            display: flex !important;
            gap: 12px !important;
            margin-top: 24px !important;
            width: 100% !important;
            padding: 0 !important;
          }
          .custom-swal-confirm-approve {
            background-color: #10b981 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 8px !important;
          }
          .custom-swal-confirm-approve:hover {
            background-color: #059669 !important;
          }
          .custom-swal-confirm-reject {
            background-color: #dc2626 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 8px !important;
          }
          .custom-swal-confirm-reject:hover {
            background-color: #b91c1c !important;
          }
          .custom-swal-cancel {
            background-color: #ffffff !important;
            color: #475569 !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          .custom-swal-cancel:hover {
            background-color: #f8fafc !important;
          }
          .custom-icon-wrapper {
            width: 64px;
            height: 64px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
          }
          .custom-swal-input {
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            padding: 10px 14px !important;
            font-size: 14px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 100% !important;
            box-sizing: border-box !important;
            margin: 16px 0 0 0 !important;
            outline: none !important;
            box-shadow: none !important;
          }
          .custom-swal-input:focus {
            border-color: #3b82f6 !important;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
          }
          .swal2-validation-message {
            background: transparent !important;
            color: #dc2626 !important;
            font-size: 13px !important;
            margin: 8px 0 0 0 !important;
            padding: 0 !important;
            font-family: 'Outfit', sans-serif !important;
            justify-content: center !important;
          }
        </style>
        <div class="custom-icon-wrapper" style="background-color: ${isApprove ? '#dcfce7' : '#fee2e2'};">
          ${isApprove
          ? `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>`
          : `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>`
        }
        </div>
        <h2 style="font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 12px 0;">
          ${isApprove ? 'Approve OT Request?' : 'Reject OT Request?'}
        </h2>
        <p style="margin: 0 0 0px 0; color: #475569; font-size: 14px; line-height: 1.5;">
          ${isApprove
          ? `Are you sure you want to approve the OT request for <strong>${record.employee}</strong>?<br/>This will allow the employee to punch in for OT.`
          : `Are you sure you want to reject the OT request for <strong>${record.employee}</strong>?`
        }
        </p>
      `
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          if (isApprove) {
            await api.post(`/overtime/requests/${record.id}/approve`, {
              approval_remarks: 'Approved by Admin'
            });
          } else {
            await api.post(`/overtime/requests/${record.id}/reject`, {
              approval_remarks: result.value
            });
          }
          toast.success(`Request ${isApprove ? 'approved' : 'rejected'} successfully`);
          fetchRequests();
        } catch (error) {
          console.error(error);
          toast.error(error.response?.data?.message || `Failed to ${action} request`);
        }
      }
    });
  };

  const hasActiveFilters = !!(filters.site || filters.date || filters.status);



  const toggleActionMenu = (id, event) => {
    if (openMenuIndex === id) {
      setOpenMenuIndex(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 5,
        left: rect.left - 130
      });
      setOpenMenuIndex(id);
    }
  };

  const handleAddClick = () => {
    setEditingRecord(null);
    setIsDrawerOpen(true);
  };

  const handleEditClick = (record) => {
    setOpenMenuIndex(null);
    setEditingRecord(record);
    setIsDrawerOpen(true);
  };

  const handleSave = (newRecord) => {
    fetchAssignments();
  };

  const handleDelete = (record) => {
    setOpenMenuIndex(null);
    MySwal.fire({
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg> Cancel Assignment</div>',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        popup: 'custom-swal-popup',
        actions: 'custom-swal-actions',
        confirmButton: 'custom-swal-confirm',
        cancelButton: 'custom-swal-cancel',
      },
      html: `
        <style>
          .custom-swal-popup {
            border-radius: 16px !important;
            padding: 32px 24px 24px 24px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 440px !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          }
          @media (max-width: 480px) {
            .custom-swal-popup {
              width: calc(100vw - 32px) !important;
              padding: 24px 16px 20px 16px !important;
            }
            .custom-swal-actions {
              flex-direction: column !important;
              gap: 8px !important;
            }
            .custom-swal-confirm,
            .custom-swal-cancel {
              width: 100% !important;
              justify-content: center !important;
            }
          }
          .custom-swal-actions {
            display: flex !important;
            gap: 12px !important;
            margin-top: 24px !important;
            width: 100% !important;
            padding: 0 !important;
          }
          .custom-swal-confirm {
            background-color: #dc2626 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 8px !important;
          }
          .custom-swal-confirm:hover {
            background-color: #b91c1c !important;
          }
          .custom-swal-cancel {
            background-color: #ffffff !important;
            color: #475569 !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          .custom-swal-cancel:hover {
            background-color: #f8fafc !important;
          }
          .custom-icon-wrapper {
            width: 64px;
            height: 64px;
            background-color: #fee2e2;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
          }
        </style>
        <div class="custom-icon-wrapper">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 12px 0;">Cancel OT Assignment?</h2>
        <p style="margin: 0 0 24px 0; color: #475569; font-size: 14px; line-height: 1.5;">
          Are you sure you want to cancel the scheduled OT for <strong>${record.employee}</strong>?<br/>
          This action cannot be undone.
        </p>
      `
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Assume delete/cancel API endpoint exists, or use the cancel status
          await api.post(`/overtime/assignments/${record.id}/cancel`);
          toast.success('Assignment cancelled successfully');
          fetchAssignments();
        } catch (error) {
          console.error(error);
          toast.error(error.response?.data?.message || 'Failed to cancel assignment');
        }
      }
    });
  };

  return (
    <div className={styles['overtime-container']} onClick={() => setOpenMenuIndex(null)}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs customItems={[
            { label: 'HR', path: '#' },
            { label: 'OT Assignments', path: '/employees/ot-assignments' }
          ]} />

          <div className={styles['page-header']}>
            <div className={styles['header-left']}>
              <h1>OT Assignments & Requests</h1>
              <p>{isAdmin ? 'Plan, assign, and approve overtime shifts for employees across project sites.' : 'View your scheduled shifts and log overtime.'}</p>
            </div>
            {isAdmin && activeTab === 'assignments' && (
              <div className={styles['header-actions']}>
                <button
                  className={styles['add-btn']}
                  style={{
                    background: 'white',
                    color: '#f97316',
                    border: '1px solid #f97316',
                    boxShadow: 'none'
                  }}
                  onClick={() => setIsCalculatorOpen(true)}
                >
                  <Calculator size={18} />
                  <span>OT Calculator</span>
                </button>
                <button className={styles['add-btn']} onClick={handleAddClick}>
                  <Plus size={18} />
                  <span>New Assignment</span>
                </button>
              </div>
            )}
          </div>

          <div className={styles['summary-collapsible']}>
            <button
              className={styles['summary-toggle']}
              onClick={() => setIsSummaryOpen(prev => !prev)}
            >
              <div className={styles['summary-toggle-left']}>
                <LayoutDashboard size={20} style={{ color: '#f97316' }} />
                <div className={styles['summary-toggle-text']}>
                  <span className={styles['summary-toggle-title']}>Summary</span>
                  <span className={styles['summary-toggle-hint']}>
                    {activeTab === 'assignments'
                      ? `${summaryData[0]?.value || '00'} scheduled · ${summaryData[1]?.value || '00'} staff`
                      : `${summaryData[0]?.value || '00'} pending · ${summaryData[1]?.value || '00'} approved`
                    }
                  </span>
                </div>
              </div>
              {isSummaryOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>

            <div className={`${styles['summary-metrics-row']} ${isSummaryOpen ? styles['summary-expanded'] : styles['summary-collapsed']}`}>
              {summaryData.map((m, i) => (
                <div key={i} className={`${styles['metric-card']} ${styles[m.theme]}`}>
                  <div className={styles['metric-icon-box']}>
                    {m.icon}
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>{m.label}</span>
                    <div className={styles['metric-value-group']}>
                      <span className={styles['metric-value']}>{m.value}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className={styles['tabs-nav']}>
            <button
              className={`${styles['tab-btn']} ${activeTab === 'assignments' ? styles['active'] : ''}`}
              onClick={() => {
                setActiveTab('assignments');
                setFilters({ site: null, date: '', status: null });
                navigate('/employees/ot-assignments', { replace: true });
              }}
            >
              <Clock size={18} />
              OT Assignments
            </button>
            <button
              className={`${styles['tab-btn']} ${activeTab === 'requests' ? styles['active'] : ''}`}
              onClick={() => {
                setActiveTab('requests');
                setFilters({ site: null, date: '', status: null });
                navigate('/employees/ot-requests', { replace: true });
              }}
            >
              <Calendar size={18} />
              OT Requests
            </button>
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search size={18} className={styles['search-icon']} />
              <input
                type="text"
                placeholder={activeTab === 'assignments' ? "Search by employee or site..." : "Search requests..."}
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: itemsPerPage, label: `${itemsPerPage} per page` }}
                  onChange={(option) => { setItemsPerPage(option.value); setCurrentPage(1); }}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                />
              </div>
              <button
                className={`${styles['filter-toggle']} ${hasActiveFilters ? styles['active'] : ''}`}
                onClick={(e) => { e.stopPropagation(); setShowFilters(!showFilters); }}
              >
                <Filters size={18} />
                <span>Filters</span>
                {hasActiveFilters && <span className={styles['filter-badge']} />}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']} onClick={(e) => e.stopPropagation()}>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Filter by Site</label>
                  <CustomSelect
                    options={siteOptions}
                    value={siteOptions.find(o => o.value === filters.site)}
                    onChange={(opt) => setFilters({ ...filters, site: opt ? opt.value : null })}
                    placeholder="Select Site"
                    isClearable
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Filter by Date</label>
                  <input
                    type="date"
                    className={styles['date-input']}
                    value={filters.date}
                    onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                  />
                </div>
                {activeTab === 'requests' && (
                  <div className={styles['filter-group']}>
                    <label>Filter by Status</label>
                    <CustomSelect
                      options={[
                        { value: 'pending', label: 'Pending' },
                        { value: 'approved', label: 'Approved' },
                        { value: 'rejected', label: 'Rejected' }
                      ]}
                      value={[
                        { value: 'pending', label: 'Pending' },
                        { value: 'approved', label: 'Approved' },
                        { value: 'rejected', label: 'Rejected' }
                      ].find(o => o.value === filters.status)}
                      onChange={(opt) => setFilters({ ...filters, status: opt ? opt.value : null })}
                      placeholder="Select Status"
                      isClearable
                    />
                  </div>
                )}
              </div>
              <div className={styles['filters-footer']}>
                <button
                  className={styles['reset-btn']}
                  onClick={() => setFilters({ site: null, date: '', status: null })}
                >
                  Reset Filters
                </button>
                <button
                  className={styles['close-filters-btn']}
                  onClick={() => setShowFilters(false)}
                >
                  Close
                </button>
              </div>
            </div>
          )}

          <div className={styles['data-view-container']}>
            <div className={styles['table-responsive']}>
              <table className={styles['data-table']}>
                {activeTab === 'assignments' ? (
                  <>
                    <thead>
                      <tr>
                        <th>Employee Details</th>
                        <th>Project Site</th>
                        <th>Scheduled Date</th>
                        <th>Assigned By</th>
                        <th>Status</th>
                        <th className={styles['actions-cell']}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                            <Loader2 size={24} className={styles['spinner']} style={{ display: 'inline-block', marginRight: '8px' }} />
                            <span>Loading OT Assignments...</span>
                          </td>
                        </tr>
                      ) : filteredAssignments.length === 0 ? (
                        <tr>
                          <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                            No OT assignments scheduled.
                          </td>
                        </tr>
                      ) : (
                        paginatedAssignments.map((record) => (
                          <tr key={record.id}>
                            <td>
                              <div className={styles['user-identity']}>
                                <div className={styles['avatar-small']}>
                                  {record.employee ? record.employee.charAt(0) : 'E'}
                                </div>
                                <div className={styles['user-info']}>
                                  <span
                                    className={`${styles['user-name']} ${styles['clickable']}`}
                                    onClick={() => navigate(`/employees/ot-assignments/view/${record.id}`)}
                                  >
                                    {record.employee}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className={styles['user-site']}>
                                <MapPin size={14} /> {record.project_site}
                              </div>
                            </td>
                            <td>
                              <div className={styles['user-site']}>
                                <Calendar size={14} /> {formatDate(record.date)}
                              </div>
                            </td>
                            <td>
                              <span className={styles['user-name']} style={{ fontSize: '13px' }}>{record.assigned_by}</span>
                            </td>
                            <td>
                              <span className={`${styles['status-badge']} ${getStatusClass(record.status)}`}>
                                {getStatusIcon(record.status)}
                                <span>{getStatusLabel(record.status)}</span>
                              </span>
                            </td>
                            <td className={styles['actions-cell']}>
                              <div className={styles['action-wrapper']}>
                                <button
                                  className={`${styles['action-btn']} ${openMenuIndex === record.id ? styles['active'] : ''}`}
                                  onClick={(e) => { e.stopPropagation(); toggleActionMenu(record.id, e); }}
                                >
                                  <MoreVertical size={18} />
                                </button>

                                {openMenuIndex === record.id && (
                                  <div
                                    className={styles['action-dropdown']}
                                    style={{ top: menuPosition.top, left: menuPosition.left }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <button className={styles['dropdown-item']} onClick={() => navigate(`/employees/ot-assignments/view/${record.id}`)}>
                                      <Eye size={16} /> View Details
                                    </button>
                                    {record.status !== 'cancelled' && (
                                      <>
                                        <button className={styles['dropdown-item']} onClick={() => handleEditClick(record)}>
                                          <Pencil size={16} /> Edit
                                        </button>
                                        <div className={styles['dropdown-divider']}></div>
                                        <button
                                          className={`${styles['dropdown-item']} ${styles['deselect']}`}
                                          onClick={() => handleDelete(record)}
                                        >
                                          <XCircle size={16} /> Cancel Assignment
                                        </button>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </>
                ) : (
                  <>
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Site & Date</th>
                        <th>Reason / Justification</th>
                        <th>Status</th>
                        <th className={styles['actions-cell']}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                            <Loader2 size={24} className={styles['spinner']} style={{ display: 'inline-block', marginRight: '8px' }} />
                            <span>Loading Overtime Requests...</span>
                          </td>
                        </tr>
                      ) : filteredRequests.length === 0 ? (
                        <tr>
                          <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                            No overtime requests found.
                          </td>
                        </tr>
                      ) : (
                        paginatedRequests.map((record) => (
                          <tr key={record.id}>
                            <td>
                              <div className={styles['user-identity']}>
                                <div className={styles['avatar-small']} style={{ background: '#e0f2fe', color: '#0284c7' }}>
                                  {record.employee ? record.employee.charAt(0) : 'E'}
                                </div>
                                <div className={styles['user-info']}>
                                  <span className={styles['user-name']}>{record.employee}</span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className={styles['user-info']}>
                                <span className={styles['user-site']} style={{ fontWeight: 600 }}>{record.project_site}</span>
                                <span className={styles['user-site']}><Calendar size={12} /> {formatDate(record.date)}</span>
                              </div>
                            </td>
                            <td>
                              <p style={{ margin: 0, fontSize: '13px', color: '#64748b', fontStyle: 'italic' }}>
                                "{record.reason}"
                              </p>
                            </td>
                            <td>
                              <span className={`${styles['status-badge']} ${getStatusClass(record.status)}`}>
                                {getStatusIcon(record.status)}
                                <span>{getStatusLabel(record.status)}</span>
                              </span>
                            </td>
                            <td className={styles['actions-cell']}>
                              {record.status?.toLowerCase() === 'pending' ? (
                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                  <button
                                    className={styles['btn-action-success']}
                                    onClick={() => handleRequestAction(record, 'approve')}
                                    title="Approve"
                                  >
                                    <CheckCircle2 size={18} />
                                  </button>
                                  <button
                                    className={styles['btn-action-danger']}
                                    onClick={() => handleRequestAction(record, 'reject')}
                                    title="Reject"
                                  >
                                    <XCircle size={18} />
                                  </button>
                                </div>
                              ) : (
                                <span style={{ fontSize: '13px', color: '#94a3b8' }}>Processed</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </>
                )}
              </table>
            </div>

            {/* Mobile View */}
            <div className={styles['po-cards']}>
              {activeTab === 'assignments' ? (
                loading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Loader2 size={32} className={styles['spinner']} style={{ display: 'inline-block', color: '#f97316' }} />
                  </div>
                ) : filteredAssignments.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                    No OT assignments scheduled.
                  </div>
                ) : (
                  paginatedAssignments.map((record) => (
                    <div key={record.id} className={styles['po-card']}>
                      <div className={styles['card-main']}>
                        <div className={styles['card-info']}>
                          <span className={styles['card-id']}>OT-{String(record.id).padStart(4, '0')}</span>
                          <span className={styles['card-amount']}>{record.employee}</span>
                        </div>
                        <div className={`action-wrapper ${styles['action-wrapper']}`}>
                          <button
                            className={`${styles['action-btn']} ${openMenuIndex === record.id ? styles['active'] : ''}`}
                            onClick={(e) => { e.stopPropagation(); toggleActionMenu(record.id, e); }}
                          >
                            <MoreVertical size={18} />
                          </button>
                          {openMenuIndex === record.id && (
                            <div
                              className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                              style={{
                                position: 'fixed',
                                top: menuPosition.top,
                                left: menuPosition.left
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button className={styles['dropdown-item']} onClick={() => navigate(`/employees/ot-assignments/view/${record.id}`)}>
                                <Eye size={16} /> View Details
                              </button>
                              <button className={styles['dropdown-item']} onClick={() => { setEditingRecord(record); setIsDrawerOpen(true); setOpenMenuIndex(null); }}>
                                <Pencil size={16} /> Edit Timings
                              </button>
                              <button className={[styles['dropdown-item'], styles['delete-opt']].join(' ')} onClick={() => { handleDelete(record); }}>
                                <Trash2 size={16} /> Cancel Assignment
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={styles['card-details']}>
                        <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                          <span className={styles['detail-label']}>Project Site</span>
                          <span className={styles['detail-value']}>{record.project_site}</span>
                        </div>
                        <div className={styles['detail-item']}>
                          <span className={styles['detail-label']}>Scheduled Date</span>
                          <span className={styles['detail-value']}>{formatDate(record.date)}</span>
                        </div>
                        <div className={styles['detail-item']}>
                          <span className={styles['detail-label']}>Timings</span>
                          <span className={styles['detail-value']}>{record.expected_start_time} - {record.expected_end_time}</span>
                        </div>
                        <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                          <span className={styles['detail-label']}>Status</span>
                          <span className={`${styles['status-badge']} ${getStatusClass(record.status)}`} style={{ minWidth: 'auto', fontSize: '10px', padding: '4px 8px', width: 'fit-content' }}>
                            {getStatusIcon(record.status)}
                            <span style={{ marginLeft: '4px' }}>{getStatusLabel(record.status)}</span>
                          </span>
                        </div>
                      </div>
                      <div className={styles['card-footer']}>
                        <div className={styles['footer-left']}>
                          <User size={14} />
                          <span>Assigned By: {record.assigned_by}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )
              ) : (
                /* Requests Mobile Cards */
                loading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Loader2 size={32} className={styles['spinner']} style={{ display: 'inline-block', color: '#f97316' }} />
                  </div>
                ) : filteredRequests.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                    No overtime requests found.
                  </div>
                ) : (
                  paginatedRequests.map((record) => (
                    <div key={record.id} className={styles['po-card']}>
                      <div className={styles['card-main']}>
                        <div className={styles['card-info']}>
                          <span className={styles['card-id']}>REQ-{String(record.id).padStart(4, '0')}</span>
                          <span className={styles['card-amount']}>{record.employee}</span>
                        </div>
                        <div className={`action-wrapper ${styles['action-wrapper']}`}>
                          {record.status?.toLowerCase() === 'pending' ? (
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                className={styles['btn-action-success']}
                                onClick={() => handleRequestAction(record, 'approve')}
                                style={{ padding: '6px', borderRadius: '6px', background: '#dcfce7', border: 'none', color: '#16a34a' }}
                              >
                                <CheckCircle2 size={16} />
                              </button>
                              <button
                                className={styles['btn-action-danger']}
                                onClick={() => handleRequestAction(record, 'reject')}
                                style={{ padding: '6px', borderRadius: '6px', background: '#fee2e2', border: 'none', color: '#dc2626' }}
                              >
                                <XCircle size={16} />
                              </button>
                            </div>
                          ) : (
                            <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 600 }}>Processed</span>
                          )}
                        </div>
                      </div>
                      <div className={styles['card-details']}>
                        <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                          <span className={styles['detail-label']}>Project Site</span>
                          <span className={styles['detail-value']}>{record.project_site}</span>
                        </div>
                        <div className={styles['detail-item']}>
                          <span className={styles['detail-label']}>Requested Date</span>
                          <span className={styles['detail-value']}>{formatDate(record.date)}</span>
                        </div>
                        <div className={styles['detail-item']}>
                          <span className={styles['detail-label']}>Timings</span>
                          <span className={styles['detail-value']}>{record.expected_start_time} - {record.expected_end_time}</span>
                        </div>
                        <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                          <span className={styles['detail-label']}>Status</span>
                          <span className={`${styles['status-badge']} ${getStatusClass(record.status)}`} style={{ minWidth: 'auto', fontSize: '10px', padding: '4px 8px', width: 'fit-content' }}>
                            {getStatusIcon(record.status)}
                            <span style={{ marginLeft: '4px' }}>{getStatusLabel(record.status)}</span>
                          </span>
                        </div>
                      </div>
                      <div className={styles['card-footer']}>
                        <div className={styles['footer-left']}>
                          <Calendar size={14} />
                          <span>Reason: {record.instructions}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )
              )}
            </div>
            
            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={currentPage}
                totalItems={activeTab === 'assignments' ? filteredAssignments.length : filteredRequests.length}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          </div>

          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={currentPage}
              totalItems={activeTab === 'assignments' ? filteredAssignments.length : filteredRequests.length}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>

          <Footer />
        </main>
      </div>

      <OTAssignmentDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        record={editingRecord}
        onSave={handleSave}
      />

      <OTSalarySummaryModal
        isOpen={isCalculatorOpen}
        onClose={() => setIsCalculatorOpen(false)}
      />
    </div>
  );
};

export default OTAssignments;


